import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { getApiUrl } from '@/lib/subdomain'

const apiUrl = getApiUrl()

export function AdminDashboard() {
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null)
  
  const refreshStatsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/refresh-stats`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to refresh stats')
      return res.json()
    },
    onSuccess: () => {
      setRefreshMessage('Stats refresh triggered! Updates will appear shortly.')
      setTimeout(() => setRefreshMessage(null), 5000)
    },
    onError: () => {
      setRefreshMessage('Failed to refresh stats')
      setTimeout(() => setRefreshMessage(null), 3000)
    },
  })

  const { data: accounts } = useQuery({
    queryKey: ['admin', 'accounts'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts`, { credentials: 'include' })
      if (!res.ok) return { accounts: [] }
      return res.json()
    },
  })

  const { data: credentials } = useQuery({
    queryKey: ['admin', 'credentials'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/credentials`, { credentials: 'include' })
      if (!res.ok) return {}
      return res.json()
    },
  })

  const steamAccount = accounts?.accounts?.find((a: any) => a.platform === 'steam')
  const epicAccount = accounts?.accounts?.find((a: any) => a.platform === 'epic')
  const xboxAccount = accounts?.accounts?.find((a: any) => a.platform === 'xbox')

  const connectedCount = [steamAccount, epicAccount, xboxAccount].filter(Boolean).length
  const credentialsCount = [credentials?.steam?.configured, credentials?.epic?.configured, credentials?.xbox?.configured].filter(Boolean).length

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold mb-2">Dashboard</h1>
        <p className="text-gray-400">Welcome back! Here's an overview of your gaming site.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Connected Accounts"
          value={connectedCount}
          total={3}
          icon="🎮"
          color="from-primary-500/20 to-purple-500/20"
          link="/accounts"
        />
        <StatCard
          title="API Credentials"
          value={credentialsCount}
          total={3}
          icon="🔑"
          color="from-green-500/20 to-emerald-500/20"
          link="/credentials"
        />
        <StatCard
          title="Site Status"
          value="Live"
          icon="🌐"
          color="from-blue-500/20 to-cyan-500/20"
          status="online"
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        
        {refreshMessage && (
          <div className={`mb-4 p-3 rounded-lg ${refreshMessage.includes('Failed') ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
            {refreshMessage}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            title="Link Steam"
            description="Add your Steam ID"
            icon="🎮"
            connected={!!steamAccount}
            link="/accounts"
          />
          <QuickAction
            title="Link Fortnite"
            description="Add Epic username"
            icon="🎯"
            connected={!!epicAccount}
            link="/accounts"
          />
          <QuickAction
            title="Link Xbox"
            description="Add Xbox gamertag"
            icon="🟢"
            connected={!!xboxAccount}
            link="/accounts"
          />
          <button
            onClick={() => refreshStatsMutation.mutate()}
            disabled={refreshStatsMutation.isPending}
            className="group text-left"
          >
            <div className="p-4 rounded-xl bg-gray-900/50 border border-gray-800 hover:border-primary-500/50 hover:bg-gray-900 transition-all">
              <div className="flex items-center gap-3">
                <div className={`text-2xl ${refreshStatsMutation.isPending ? 'animate-spin' : ''}`}>
                  {refreshStatsMutation.isPending ? '⏳' : '🔄'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">Refresh Stats</p>
                  <p className="text-sm text-gray-500 truncate">
                    {refreshStatsMutation.isPending ? 'Updating...' : 'Fetch latest stats now'}
                  </p>
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Connected Platforms */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Platform Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlatformStatus
            name="Steam"
            icon="🎮"
            connected={!!steamAccount}
            username={steamAccount?.platform_username}
            color="bg-[#1b2838]"
          />
          <PlatformStatus
            name="Fortnite"
            icon="🎯"
            connected={!!epicAccount}
            username={epicAccount?.platform_username}
            color="bg-gradient-to-r from-[#9d4dbb] to-[#5c3d7a]"
          />
          <PlatformStatus
            name="Xbox"
            icon="🟢"
            connected={!!xboxAccount}
            username={xboxAccount?.platform_username}
            color="bg-[#107c10]"
          />
        </div>
      </div>
    </div>
  )
}

function StatCard({ 
  title, 
  value, 
  total, 
  icon, 
  color, 
  link,
  status 
}: { 
  title: string
  value: string | number
  total?: number
  icon: string
  color: string
  link?: string
  status?: 'online' | 'offline'
}) {
  const content = (
    <div className={`relative overflow-hidden rounded-xl p-6 bg-gradient-to-br ${color} border border-gray-800 hover:border-gray-700 transition-colors`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold">{value}</span>
            {total && <span className="text-gray-500">/ {total}</span>}
            {status === 'online' && (
              <span className="ml-2 flex items-center text-green-400 text-sm">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse" />
                Online
              </span>
            )}
          </div>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  )

  if (link) {
    return <Link to={link}>{content}</Link>
  }
  return content
}

function QuickAction({
  title,
  description,
  icon,
  connected,
  link,
  external
}: {
  title: string
  description: string
  icon: string
  connected?: boolean
  link: string
  external?: boolean
}) {
  const Component = external ? 'a' : Link
  const props = external ? { href: link, target: '_blank', rel: 'noopener' } : { to: link }

  return (
    <Component {...props as any} className="group">
      <div className="p-4 rounded-xl bg-gray-900/50 border border-gray-800 hover:border-primary-500/50 hover:bg-gray-900 transition-all">
        <div className="flex items-center gap-3">
          <div className="text-2xl">{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium truncate">{title}</p>
              {connected && (
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  Connected
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">{description}</p>
          </div>
          <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-400 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Component>
  )
}

function PlatformStatus({
  name,
  icon,
  connected,
  username,
  color
}: {
  name: string
  icon: string
  connected: boolean
  username?: string
  color: string
}) {
  return (
    <div className={`relative overflow-hidden rounded-xl p-4 ${color} ${connected ? 'opacity-100' : 'opacity-50'}`}>
      <div className="flex items-center gap-3">
        <div className="text-2xl">{icon}</div>
        <div className="flex-1">
          <p className="font-semibold">{name}</p>
          {connected ? (
            <p className="text-sm text-white/80">{username}</p>
          ) : (
            <p className="text-sm text-white/50">Not connected</p>
          )}
        </div>
        {connected && (
          <div className="w-3 h-3 bg-green-400 rounded-full" />
        )}
      </div>
    </div>
  )
}
